/**
 * API client — points to the live DIKDOC backend (or falls back to mock).
 *
 * VITE_API_URL can be set; default is http://localhost:4000/api
 * In production, set VITE_API_URL to your deployed API endpoint.
 *
 * v9.0.1: When DIKDOC_API_DEGRADED=true, the client returns deterministic
 * in-memory fallback data derived from the reference catalogues. This
 * ensures UI never shows blank screens when the backend is unreachable.
 */
import { fallbackApi } from "./with-fallback";
import { synthLabResults, synthPatients, synthEncounters, synthPharmacyStock, synthBillingClaims, synthImagingStudies, synthAlerts } from "./synth-data";

const BASE = (import.meta.env.VITE_API_URL as string) || "http://localhost:4000/api";
const DEGRADED = (import.meta.env.VITE_API_DEGRADED as string) === "true" || (import.meta.env.DIKDOC_API_DEGRADED as string) === "true";

interface FetchOpts {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  token?: string;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public data?: unknown) {
    super(message);
  }
}

export async function apiFetch<T = unknown>(path: string, opts: FetchOpts = {}): Promise<T> {
  const { method = "GET", body, query, token } = opts;
  let url = `${BASE}${path.startsWith("/") ? path : `/${path}`}`;
  if (query) {
    const qs = Object.entries(query)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join("&");
    if (qs) url += `?${qs}`;
  }
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  else {
    try {
      const stored = localStorage.getItem("auth_token");
      if (stored) headers.Authorization = `Bearer ${stored}`;
    } catch {}
  }
  const tenantId = (() => {
    try { return localStorage.getItem("tenant_id") || "default"; } catch { return "default"; }
  })();
  headers["X-Tenant-Id"] = tenantId;

  // In degraded mode, return in-memory fallback synchronously (no fetch)
  if (DEGRADED) {
    return mapPathToFallback(path) as T;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let payload: any = null;
    try { payload = await res.json(); } catch {}
    throw new ApiError(res.status, payload?.error?.message || payload?.message || res.statusText, payload);
  }
  return res.json();
}

// Map API path → fallback dataset
function mapPathToFallback(path: string): any {
  if (path.includes("/health")) return fallbackApi.health?.() ?? { db: "in-memory", status: "ok" };
  // List endpoints — return synthetic items
  if (path.includes("/lab/results") || path.includes("/lab/abnormal")) {
    return { items: synthLabResults(40), total: 1840, db: "in-memory" };
  }
  if (path.startsWith("/patients") || path.includes("/patients?")) {
    return { items: synthPatients(50), total: 104, db: "in-memory" };
  }
  if (path.includes("/encounters") && !path.includes("/stats")) {
    return { items: synthEncounters(50), total: 312, db: "in-memory" };
  }
  if (path.includes("/pharmacy")) {
    return { items: synthPharmacyStock(50), total: 104, db: "in-memory" };
  }
  if (path.includes("/billing") && !path.includes("/stats") && !path.includes("/revenue")) {
    return { items: synthBillingClaims(30), total: 287, db: "in-memory" };
  }
  if (path.includes("/imaging") || path.includes("/dicom")) {
    return { items: synthImagingStudies(30), total: 412, db: "in-memory" };
  }
  if (path.includes("/alerts") && !path.includes("/stats")) {
    return { items: synthAlerts(15), total: 14, db: "in-memory" };
  }
  // Stats endpoints
  if (path.includes("/patients/stats")) return fallbackApi.patientStats();
  if (path.includes("/encounters/stats")) return fallbackApi.encounterStats();
  if (path.includes("/lab/stats")) return fallbackApi.labStats();
  if (path.includes("/pharmacy/stats")) return fallbackApi.pharmacyStats();
  if (path.includes("/billing/stats")) return fallbackApi.billingStats();
  if (path.includes("/billing/revenue")) return fallbackApi.cockpitStats();
  if (path.includes("/alerts/stats")) return fallbackApi.alertsStats();
  if (path.includes("/audit/stats")) return fallbackApi.auditStats();
  if (path.includes("/imaging/stats")) return fallbackApi.imagingStats();
  if (path.includes("/users/stats")) return fallbackApi.userStats();
  return { db: "in-memory", total: 0, items: [] };
}

// Convenience helpers per domain
export const api = {
  health: () => apiFetch("/health"),
  dbMode: () => apiFetch("/live/db-mode"),
  users: (q?: { limit?: number; search?: string }) => apiFetch("/users", { query: q }),
  user: (id: number) => apiFetch(`/users/${id}`),
  userStats: () => apiFetch("/users/stats"),
  patients: (q?: { limit?: number; offset?: number; search?: string }) => apiFetch("/patients", { query: q }),
  patient: (id: number) => apiFetch(`/patients/${id}`),
  patientStats: () => apiFetch("/patients/stats"),
  encounters: (q?: { limit?: number; status?: string }) => apiFetch("/encounters", { query: q }),
  encountersActive: () => apiFetch("/encounters/active"),
  encounterStats: () => apiFetch("/encounters/stats"),
  labResults: (q?: { limit?: number; status?: string }) => apiFetch("/lab/results", { query: q }),
  labAbnormal: () => apiFetch("/lab/abnormal"),
  labStats: () => apiFetch("/lab/stats"),
  pharmacyStock: (q?: { lowOnly?: boolean }) => apiFetch("/pharmacy/stock", { query: q }),
  pharmacyControlled: () => apiFetch("/pharmacy/controlled"),
  pharmacyDispensations: (q?: { limit?: number }) => apiFetch("/pharmacy/dispensations", { query: q }),
  pharmacyStats: () => apiFetch("/pharmacy/stats"),
  claims: (q?: { limit?: number; status?: string }) => apiFetch("/billing/claims", { query: q }),
  billingStats: () => apiFetch("/billing/stats"),
  billingRevenue: () => apiFetch("/billing/revenue"),
  alerts: (q?: { limit?: number }) => apiFetch("/alerts", { query: q }),
  alertsActive: () => apiFetch("/alerts/active"),
  alertsStats: () => apiFetch("/alerts/stats"),
  ackAlert: (id: number) => apiFetch(`/alerts/${id}/acknowledge`, { method: "POST" }),
  auditEvents: (q?: { limit?: number }) => apiFetch("/audit/events", { query: q }),
  auditVerify: () => apiFetch("/audit/verify"),
  auditStats: () => apiFetch("/audit/stats"),
  observabilityLive: () => apiFetch("/observability/live"),
  // v7.5 — Referentials
  medications: (q?: { q?: string; atc_prefix?: string }) => apiFetch("/referentials/medications", { query: q }),
  medicalDevices: (q?: { q?: string; category?: string }) => apiFetch("/referentials/medical-devices", { query: q }),
  ngap: (q?: { q?: string; category?: string }) => apiFetch("/referentials/ngap", { query: q }),
  labDrivers: () => apiFetch("/referentials/lab-drivers"),
  imagingDrivers: () => apiFetch("/referentials/imaging-drivers"),
  referentialsStats: () => apiFetch("/referentials/stats"),
  rbacRoles: () => apiFetch("/rbac/roles"),
  rbacCheck: (role: string, resource: string, action: string) =>
    apiFetch("/rbac/check", { method: "POST", body: { role, resource, action } }),
  // v7.5 — Kafka
  kafkaTopics: () => apiFetch("/kafka/topics"),
  kafkaLag: () => apiFetch("/kafka/consumer-lag"),
  kafkaThroughput: () => apiFetch("/kafka/throughput"),
  observationsRecent: (q?: { limit?: number }) => apiFetch("/observations/recent", { query: q }),
  observationsLiveCount: () => apiFetch("/observations/live-count"),
  socThreats: () => apiFetch("/soc/threats"),
  socMetrics: () => apiFetch("/soc/metrics"),
  socEventsFeed: () => apiFetch("/soc/events-feed"),
  observabilityStatus: () => apiFetch("/observability/status"),
  icd10Suggest: (q: string, topK = 5) => apiFetch("/ai/icd10", { query: { q, topK } }),
  startWorkflow: (def: unknown) => apiFetch("/workflows/start", { method: "POST", body: def }),
  workflowStatus: (id: string) => apiFetch(`/workflows/runs/${id}`),
};

export default api;